import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Trash2, Megaphone, Target, DollarSign, TrendingUp, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  idea: "pill-gray",
  planeacion: "pill-blue",
  activa: "pill-green",
  pausada: "pill-yellow",
  terminada: "pill-dark",
};

const PRESET_COLORS = ["#D99D26", "#0A0A0A", "#8a5e10", "#666666", "#1f3f8a", "#1e6b32", "#a31c1c"];
const BRANCHES = ["Tampico", "Tampico Aldama", "Pánuco", "Cd. Madero", "Altamira", "App", "Web", "TikTok Shop"];

const Campaigns = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("new") === "1") setShowForm(true);
  }, [params]);

  const load = () => {
    setLoading(true);
    api.get("/campaigns").then((res) => setItems(res.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <Layout
      title="Campañas"
      subtitle="Estrategia 360"
      actions={
        <button onClick={() => setShowForm(true)} data-testid="new-campaign-btn" className="btn-primary flex items-center gap-2">
          <Plus size={14} /> Nueva campaña
        </button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : items.length === 0 ? (
        <EmptyState onCreate={() => setShowForm(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="campaigns-grid">
          {items.map((c) => <CampaignCard key={c.id} campaign={c} onChange={load} />)}
        </div>
      )}

      {showForm && (
        <CampaignFormModal
          onClose={() => setShowForm(false)}
          onCreated={() => {
            setShowForm(false);
            load();
          }}
        />
      )}
    </Layout>
  );
};

const EmptyState = ({ onCreate }) => (
  <div className="aura-card-flat p-12 text-center">
    <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
      <Megaphone size={26} />
    </div>
    <h3 className="font-display text-2xl font-medium">Aún no hay campañas</h3>
    <p className="text-[#666] mt-2 mb-5 max-w-md mx-auto">
      Crea tu primera campaña 360 para orquestar guiones, copies, diseños y publicaciones.
    </p>
    <button onClick={onCreate} className="btn-primary">Crear primera campaña</button>
  </div>
);

const CampaignCard = ({ campaign, onChange }) => {
  const [progress, setProgress] = useState(campaign.progress || 0);

  const updateProgress = async (val) => {
    setProgress(val);
    await api.patch(`/campaigns/${campaign.id}`, { progress: val });
    onChange();
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar esta campaña?")) return;
    await api.delete(`/campaigns/${campaign.id}`);
    toast.success("Campaña eliminada");
    onChange();
  };

  return (
    <div className="aura-card p-5 relative group flex flex-col" data-testid={`campaign-${campaign.id}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="w-10 h-10 rounded-xl" style={{ background: campaign.color || "#D99D26" }} />
        <span className={`pill ${STATUS_COLORS[campaign.status] || "pill-gray"}`}>{campaign.status}</span>
      </div>
      <h3 className="font-display text-lg font-medium leading-tight">{campaign.name}</h3>
      {campaign.objective && (
        <p className="text-sm text-[#666] mt-1 line-clamp-2">{campaign.objective}</p>
      )}

      {/* KPIs grid */}
      {(campaign.budget_mxn || campaign.reach_target || campaign.sales_target || campaign.roas_target) ? (
        <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
          {campaign.budget_mxn > 0 && (
            <div className="bg-[#FAFAFA] rounded-lg p-2">
              <div className="text-[10px] text-[#666] flex items-center gap-1"><DollarSign size={10} /> Presupuesto</div>
              <div className="font-display font-medium">${campaign.budget_mxn?.toLocaleString("es-MX")}</div>
            </div>
          )}
          {campaign.reach_target > 0 && (
            <div className="bg-[#FAFAFA] rounded-lg p-2">
              <div className="text-[10px] text-[#666] flex items-center gap-1"><Target size={10} /> Alcance</div>
              <div className="font-display font-medium">{campaign.reach_target?.toLocaleString("es-MX")}</div>
            </div>
          )}
          {campaign.sales_target > 0 && (
            <div className="bg-[#FAFAFA] rounded-lg p-2">
              <div className="text-[10px] text-[#666]">🛒 Ventas meta</div>
              <div className="font-display font-medium">{campaign.sales_target}</div>
            </div>
          )}
          {campaign.roas_target > 0 && (
            <div className="bg-[#FAFAFA] rounded-lg p-2">
              <div className="text-[10px] text-[#666] flex items-center gap-1"><TrendingUp size={10} /> ROAS</div>
              <div className="font-display font-medium">{campaign.roas_target}x</div>
            </div>
          )}
        </div>
      ) : null}

      <div className="mt-4">
        <div className="flex justify-between text-xs text-[#666] mb-1">
          <span>Avance</span>
          <span className="font-medium text-[#0A0A0A]">{progress}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="5"
          value={progress}
          onChange={(e) => updateProgress(parseInt(e.target.value))}
          className="w-full accent-[#D99D26]"
          data-testid={`progress-${campaign.id}`}
        />
      </div>
      <div className="flex flex-wrap gap-1 mt-3">
        {(campaign.channels || []).slice(0, 5).map((ch) => (
          <span key={ch} className="pill pill-gray text-[10px]">{ch}</span>
        ))}
      </div>
      {(campaign.branches || []).length > 0 && (
        <div className="text-[10px] text-[#666] mt-2">📍 {campaign.branches.join(" · ")}</div>
      )}

      <Link to={`/campaigns/${campaign.id}`} className="mt-4 btn-outline text-xs flex items-center justify-center gap-1 w-full" data-testid={`open-command-${campaign.id}`}>
        Abrir Comando de Misión <ArrowRight size={12} />
      </Link>

      <button
        onClick={remove}
        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#666] hover:text-red-600 p-1"
        data-testid={`delete-campaign-${campaign.id}`}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

const CampaignFormModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "",
    description: "",
    objective: "",
    status: "activa",
    color: "#D99D26",
    channels: [],
    audience: "",
    kpi_goal: "",
    start_date: "",
    end_date: "",
    budget_mxn: 0,
    reach_target: 0,
    sales_target: 0,
    roas_target: 0,
    leads_target: 0,
    branches: [],
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/campaigns", {
        ...form,
        budget_mxn: Number(form.budget_mxn) || 0,
        reach_target: Number(form.reach_target) || 0,
        sales_target: Number(form.sales_target) || 0,
        roas_target: Number(form.roas_target) || 0,
        leads_target: Number(form.leads_target) || 0,
      });
      toast.success("Campaña creada");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (ch) => {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(ch) ? f.channels.filter((c) => c !== ch) : [...f.channels, ch],
    }));
  };

  const toggleBranch = (b) => {
    setForm((f) => ({
      ...f,
      branches: f.branches.includes(b) ? f.branches.filter((x) => x !== b) : [...f.branches, b],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-7 space-y-4"
        data-testid="campaign-form"
      >
        <div className="flex justify-between items-start">
          <div>
            <div className="label-overline">Crear</div>
            <h2 className="font-display text-2xl font-medium">Nueva campaña 360</h2>
          </div>
          <button type="button" onClick={onClose} className="text-[#666] hover:text-[#0A0A0A]"><X size={18} /></button>
        </div>

        <div>
          <label className="aura-label">Nombre *</label>
          <input className="aura-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Promo 4x3 verano" data-testid="campaign-name" />
        </div>
        <div>
          <label className="aura-label">Descripción</label>
          <textarea className="aura-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div>
          <label className="aura-label">Objetivo</label>
          <input className="aura-input" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} placeholder="Aumentar ventas X%" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="aura-label">Estado</label>
            <select className="aura-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="idea">Idea</option>
              <option value="planeacion">Planeación</option>
              <option value="activa">Activa</option>
              <option value="pausada">Pausada</option>
              <option value="terminada">Terminada</option>
            </select>
          </div>
          <div>
            <label className="aura-label">Color</label>
            <div className="flex gap-2 flex-wrap pt-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  className={`w-8 h-8 rounded-lg border-2 ${form.color === c ? "border-[#D99D26] scale-110" : "border-transparent"} transition-transform`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
        </div>
        <div>
          <label className="aura-label">Canales</label>
          <div className="flex flex-wrap gap-2">
            {["instagram", "tiktok", "facebook", "youtube_shorts", "whatsapp", "pauta", "app", "web", "sucursal", "tiktok_shop"].map((ch) => (
              <button
                type="button"
                key={ch}
                onClick={() => toggleChannel(ch)}
                className={`pill cursor-pointer ${form.channels.includes(ch) ? "pill-yellow" : "pill-gray"}`}
              >
                {ch}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="aura-label">Audiencia</label>
            <input className="aura-input" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} placeholder="Mujeres y hombres 18-45..." />
          </div>
          <div>
            <label className="aura-label">KPI principal (texto)</label>
            <input className="aura-input" value={form.kpi_goal} onChange={(e) => setForm({ ...form, kpi_goal: e.target.value })} placeholder="+10% ventas" />
          </div>
        </div>

        {/* KPIs cuantitativos */}
        <div className="border-t border-[#E5E5E5] pt-4">
          <div className="label-overline mb-3">🎯 KPIs y presupuesto</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="aura-label text-[10px]">Presupuesto MXN</label>
              <input type="number" className="aura-input" value={form.budget_mxn} onChange={(e) => setForm({ ...form, budget_mxn: e.target.value })} placeholder="25000" data-testid="campaign-budget" />
            </div>
            <div>
              <label className="aura-label text-[10px]">Alcance meta</label>
              <input type="number" className="aura-input" value={form.reach_target} onChange={(e) => setForm({ ...form, reach_target: e.target.value })} placeholder="500000" data-testid="campaign-reach" />
            </div>
            <div>
              <label className="aura-label text-[10px]">Ventas meta (uds)</label>
              <input type="number" className="aura-input" value={form.sales_target} onChange={(e) => setForm({ ...form, sales_target: e.target.value })} placeholder="200" />
            </div>
            <div>
              <label className="aura-label text-[10px]">ROAS meta (x)</label>
              <input type="number" step="0.1" className="aura-input" value={form.roas_target} onChange={(e) => setForm({ ...form, roas_target: e.target.value })} placeholder="3.5" />
            </div>
            <div>
              <label className="aura-label text-[10px]">Leads meta</label>
              <input type="number" className="aura-input" value={form.leads_target} onChange={(e) => setForm({ ...form, leads_target: e.target.value })} placeholder="500" />
            </div>
          </div>
        </div>

        {/* Fechas */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="aura-label">Inicio</label>
            <input type="date" className="aura-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <label className="aura-label">Fin</label>
            <input type="date" className="aura-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
        </div>

        {/* Sucursales */}
        <div>
          <label className="aura-label">📍 Sucursales / canales propios</label>
          <p className="text-xs text-[#666] mb-2">Marca las que participan. Próximamente cada una verá sus KPIs por separado con Meta Ads.</p>
          <div className="flex flex-wrap gap-2">
            {BRANCHES.map((b) => (
              <button type="button" key={b} onClick={() => toggleBranch(b)} className={`pill cursor-pointer ${form.branches.includes(b) ? "pill-yellow" : "pill-gray"}`}>{b}</button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center flex items-center gap-2" data-testid="campaign-submit">
            {saving && <span className="spinner" />}
            {saving ? "Creando..." : "Crear campaña"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Campaigns;
